import tilebelt from "@mapbox/tilebelt";
// @ts-ignore
import tileDecode from "arcgis-pbf-parser";
import fetch from "isomorphic-unfetch";
import { ArcgisOptions } from "./types/ArcgisOptions";
import { GeoJsonSourceOptions } from "./types/GeoJsonSourceOptions";

/**
 * A class for retrieving features from an ArcGIS [FeatureServer](https://developers.arcgis.com/rest/services-reference/enterprise/feature-service.htm) or [MapServer](https://developers.arcgis.com/rest/services-reference/enterprise/map-service.htm).
 * @class
 * @classdesc Retrieves features from an ArcGIS FeatureServer or MapServer by making tiled requests rather than simply requesting every feature. By default it requests data using the arcgis [`pbf`](https://developers.arcgis.com/rest/services-reference/enterprise/output-formats.htm#:~:text=27%26f%3Dgeojson-,PBF,-Parameter%20format) format to minimize payload size, however, if that is not available it attempts to use the [`geojson`](https://developers.arcgis.com/rest/services-reference/enterprise/output-formats.htm#:~:text=services%3Ff%3Dpjson-,GeoJSON,-Parameter%20format) format.
 *
 * > **Note** This class is compatible with both [mapbox-gl](https://github.com/mapbox/mapbox-gl-js) and [maplibre-gl](https://github.com/maplibre/maplibre-gl-js).
 *
 * **ArcGIS Compatibility**: This class relies on ArcGIS Enterprise/Server v10.4+.
 * - In v10.4 (Feb 2016) the `geojson` format was added as a supported format.
 * - In v10.7 (Mar 2019) the `pbf` format was added. ArcGIS Online meets these requirements.
 *
 * @example
import {FeatureService} from '@areahub/lib'

const fsSourceId = 'feature-server-src'

const service = new FeatureService(fsSourceId, map, {
  url: 'https://portal.spatial.nsw.gov.au/server/rest/services/NSW_Administrative_Boundaries_Theme/FeatureServer/6'
})

 */
export class FeatureService {
  private sourceId: string;
  private _map: any;
  private _tileIndices: Map<any, any>;
  private _featureIndices: Map<any, any>;
  public _featureCollections: Map<any, any>;
  private _esriServiceOptions: any;
  private _fallbackProjectionEndpoint: string;
  /** Returns the service endpoint metadata as JSON. */
  private serviceMetadata: any;
  private _maxExtent: any;
  private _boundEvent: any;
  private _geometryOptions: ArcgisOptions["geometryOptions"] | null;

  constructor(
    /** Name of a source description to be used for this layer. Required for all layer types except background.
     * @see [Layers | Style Specification | Mapbox GL JS | Mapbox](https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#source)
     */
    sourceId: string,
    /* A mapbox-gl or maplibre-gl map instance. */
    map: any,
    /* A range of options which will be used to manage the arcgis service.  */
    arcgisOptions: ArcgisOptions,
    /* An object which will be passed to the creation of the mapbox-gl [geojson source](https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/#geojson)   */
    geojsonSourceOptions?: GeoJsonSourceOptions,
  ) {
    // if (!sourceId || !map || !arcgisOptions?.url)
    //   throw new Error(
    //     'Source id, map and arcgisOptions must be supplied as the first three arguments.'
    //   );
    if (!arcgisOptions.url)
      throw new Error(
        "A url must be supplied as part of the esriServiceOptions object.",
      );

    this.sourceId = sourceId;
    this._map = map;

    this._tileIndices = new Map();
    this._featureIndices = new Map();
    this._featureCollections = new Map();

    this._esriServiceOptions = Object.assign(
      {
        useStaticZoomLevel: false,
        minZoom: arcgisOptions.useStaticZoomLevel ? 7 : 2,
        simplifyFactor: 0.3,
        precision: 8,
        where: "1=1",
        to: null,
        from: null,
        outFields: "*",
        setAttributionFromService: false,
        f: "pbf",
        useServiceBounds: true,
        projectionEndpoint: `${
          arcgisOptions.url.split("rest/services")[0]
        }rest/services/Geometry/GeometryServer/project`,
      },
      arcgisOptions,
    );
    this._geometryOptions =
      typeof arcgisOptions?.geometryOptions !== "undefined"
        ? arcgisOptions.geometryOptions
        : null;

    this._fallbackProjectionEndpoint =
      "https://tasks.arcgisonline.com/arcgis/rest/services/Geometry/GeometryServer/project";
    this.serviceMetadata = null;
    this._maxExtent = [-Infinity, Infinity, -Infinity, Infinity];

    const gjOptions = !geojsonSourceOptions ? {} : geojsonSourceOptions;
    this._map?.addSource(
      sourceId,
      Object.assign(gjOptions, {
        type: "geojson",
        data: this._getBlankFc(),
      }),
    );

    this._getServiceMetadata().then(() => {
      if (!this.supportsPbf) {
        if (!this.supportsGeojson) {
          this._map.removeSource(sourceId);
          throw new Error(
            "Server does not support PBF or GeoJSON query formats.",
          );
        }
        this._esriServiceOptions.f = "geojson";
      }

      if (this._esriServiceOptions.useServiceBounds) {
        const serviceExtent = this.serviceMetadata.extent;
        if (serviceExtent?.spatialReference?.wkid === 4326) {
          this._setBounds([
            serviceExtent.xmin,
            serviceExtent.ymin,
            serviceExtent.xmax,
            serviceExtent.ymax,
          ]);
          this._clearAndRefreshTiles();
        } else {
          this._projectBounds();
        }
      }

      if (this._esriServiceOptions.outFields !== "*") {
        this._esriServiceOptions.outFields = `${this._esriServiceOptions.outFields},${this.serviceMetadata.uniqueIdField.name}`;
      }

      this.enableRequests();
    });
  }

  /**
   * __Important__ The destroySource() method removes the source from the map and associated event listeners for retrieving data which
   * request data as the map is panned, so it's important to call this method if removing the layer from the map completely.
   */
  destroySource() {
    this.disableRequests();
    this._map.removeSource(this.sourceId);
  }

  _getBlankFc() {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  _setBounds(bounds: any) {
    this._maxExtent = bounds;
  }

  // @ts-ignore
  get supportsGeojson() {
    return this.serviceMetadata.supportedQueryFormats.indexOf("geoJSON") > -1;
  }

  // @ts-ignore
  get supportsPbf() {
    return this.serviceMetadata.supportedQueryFormats?.indexOf("PBF") > -1;
  }

  /**
   * **Important** The disableRequests() method temporarily disables the associated event listeners for retrieving data which request
   * data as the map is panned, you may want to call this if you toggle your layer off.
   */
  disableRequests() {
    this._map.off("moveend", this._boundEvent);
  }

  /**
   * __Important__ The enableRequests() method enables the associated event listeners for retrieving data which request data as the map is
   * panned. By default this is called by the constructor.
   */
  enableRequests() {
    this._boundEvent = this._findAndMapData.bind(this);
    this._map?.on("moveend", this._boundEvent);
  }

  _clearAndRefreshTiles() {
    this._tileIndices = new Map();
    this._featureIndices = new Map();
    this._featureCollections = new Map();
    this._findAndMapData();
  }

  setWhere(newWhere: any) {
    this._esriServiceOptions.where = newWhere;
    this._clearAndRefreshTiles();
  }

  clearWhere() {
    this._esriServiceOptions.where = "1=1";
    this._clearAndRefreshTiles();
  }

  setDate(to: Date, from: Date) {
    this._esriServiceOptions.to = to;
    this._esriServiceOptions.from = from;
    this._clearAndRefreshTiles();
  }

  _createOrGetTileIndex(zoomLevel: number) {
    const existingZoomIndex = this._tileIndices.get(zoomLevel);
    if (existingZoomIndex) return existingZoomIndex;
    const newIndex = new Map();
    this._tileIndices.set(zoomLevel, newIndex);
    return newIndex;
  }

  _createOrGetFeatureCollection(zoomLevel: number) {
    const existingZoomIndex = this._featureCollections.get(zoomLevel);
    if (existingZoomIndex) return existingZoomIndex;
    const fc = this._getBlankFc();
    this._featureCollections.set(zoomLevel, fc);
    return fc;
  }

  _createOrGetFeatureIdIndex(zoomLevel: number) {
    const existingFeatureIdIndex = this._featureIndices.get(zoomLevel);
    if (existingFeatureIdIndex) return existingFeatureIdIndex;
    const newFeatureIdIndex = new Map();
    this._featureIndices.set(zoomLevel, newFeatureIdIndex);
    return newFeatureIdIndex;
  }

  async _findAndMapData() {
    const z = this._map?.getZoom();

    if (z < this._esriServiceOptions.minZoom) {
      return;
    }
    const bounds = this._map?.getBounds().toArray();
    const primaryTile = tilebelt.bboxToTile([
      bounds[0][0],
      bounds[0][1],
      bounds[1][0],
      bounds[1][1],
    ]);

    if (
      this._esriServiceOptions.useServiceBounds &&
      !this._doesTileOverlapBbox(this._maxExtent, bounds)
    ) {
      return;
    }

    // If we're not using a static zoom level we'll round to the nearest even zoom level
    // This means we don't need to request new data for every zoom level allowing us to reuse the previous levels data
    const zoomLevel = this._esriServiceOptions.useStaticZoomLevel
      ? this._esriServiceOptions.minZoom
      : 2 * Math.floor(z / 2);
    const zoomLevelIndex = this._createOrGetTileIndex(zoomLevel);
    const featureIdIndex = this._createOrGetFeatureIdIndex(zoomLevel);
    const fc = this._createOrGetFeatureCollection(zoomLevel);

    const tilesToRequest: any = [];

    if (primaryTile[2] < zoomLevel) {
      let candidateTiles = tilebelt.getChildren(primaryTile);
      let minZoomOfCandidates = candidateTiles[0][2];
      while (minZoomOfCandidates < zoomLevel) {
        const newCandidateTiles: any = [];
        candidateTiles.forEach((t) =>
          newCandidateTiles.push(...tilebelt.getChildren(t)),
        );
        candidateTiles = newCandidateTiles;
        minZoomOfCandidates = candidateTiles[0][2];
      }

      for (let index = 0; index < candidateTiles.length; index++) {
        if (this._doesTileOverlapBbox(candidateTiles[index], bounds)) {
          tilesToRequest.push(candidateTiles[index]);
        }
      }
    } else {
      tilesToRequest.push(primaryTile);
    }

    for (let index = 0; index < tilesToRequest.length; index++) {
      const quadKey = tilebelt.tileToQuadkey(tilesToRequest[index]);
      if (zoomLevelIndex.has(quadKey)) {
        tilesToRequest.splice(index, 1);
        index--;
      } else zoomLevelIndex.set(quadKey, true);
    }

    if (tilesToRequest.length === 0) {
      this._updateFcOnMap(fc);
      return;
    }

    // This tolerance will be used to inform the quantization/simplification of features
    const mapWidth = Math.abs(bounds[1][0] - bounds[0][0]);
    const tolerance =
      (mapWidth / this._map.getCanvas().width) *
      this._esriServiceOptions.simplifyFactor;
    await this._loadTiles(tilesToRequest, tolerance, featureIdIndex, fc);
    this._updateFcOnMap(fc);
  }

  async _loadTiles(
    tilesToRequest: any[],
    tolerance: number,
    featureIdIndex: any,
    fc: any,
  ) {
    return new Promise((resolve) => {
      const promises = tilesToRequest.map((t: any) =>
        this._getTile(t, tolerance),
      );
      Promise.all(promises).then((featureCollections) => {
        featureCollections.forEach((tileFc) => {
          if (tileFc) this._iterateItems(tileFc, featureIdIndex, fc);
        });
        // @ts-ignore
        resolve();
      });
    });
  }

  _iterateItems(
    tileFc: unknown,
    featureIdIndex: { has: (arg0: any) => any; set: (arg0: any) => void },
    fc: { features: any[] },
  ) {
    // @ts-ignore
    tileFc.features.forEach((feature: { id: any }) => {
      if (!featureIdIndex.has(feature.id)) {
        fc.features.push(feature);
        featureIdIndex.set(feature.id);
      }
    });
  }
  // @ts-ignore
  get _time() {
    if (!this._esriServiceOptions.to) return false;
    let from = this._esriServiceOptions.from;
    let to = this._esriServiceOptions.to;
    if (from instanceof Date) from = from.valueOf();
    if (to instanceof Date) to = to.valueOf();

    return `${from},${to}`;
  }

  _getTile(tile: number[], tolerance: any) {
    const tileBounds = tilebelt.tileToBBOX(tile);

    const extent = {
      spatialReference: {
        latestWkid: 4326,
        wkid: 4326,
      },
      xmin: tileBounds[0],
      ymin: tileBounds[1],
      xmax: tileBounds[2],
      ymax: tileBounds[3],
    };
    const geometryOptions = this._geometryOptions
      ? this.getGeometryOptions()
      : {};
    // @ts-ignore
    const params = new URLSearchParams({
      f: this._esriServiceOptions.f,
      geometry: JSON.stringify(extent),
      where: this._esriServiceOptions.where,
      outFields: this._esriServiceOptions.outFields,
      outSR: 4326,
      returnZ: false,
      returnM: false,
      precision: this._esriServiceOptions.precision,
      quantizationParameters: JSON.stringify({
        extent,
        tolerance,
        mode: "view",
      }),
      resultType: "tile",
      spatialRel: "esriSpatialRelIntersects",
      geometryType: "esriGeometryEnvelope",
      inSR: 4326,
      ...geometryOptions,
    });

    if (this._time) params.append("time", this._time);

    return new Promise((resolve) => {
      fetch(`${`${this._esriServiceOptions.url}/query?${params.toString()}`}`, {
        cache: "force-cache",
      })
        .then((response) => {
          //eslint-disable-line
          return this._esriServiceOptions.f === "pbf"
            ? response.arrayBuffer()
            : response.json();
        })
        .then((data) => {
          let out;
          try {
            out =
              this._esriServiceOptions.f === "pbf"
                ? tileDecode(new Uint8Array(data)).featureCollection
                : data;
          } catch (err) {
            console.error(
              "Could not parse arcgis buffer. Please check the url you requested.",
            );
          }
          resolve(out);
        });
    });
  }

  _updateFcOnMap(fc: any) {
    // @ts-ignore
    if (this?._map) {
      this._map?.getSource(this.sourceId)?.setData(fc);
    }
  }

  _doesTileOverlapBbox(tile: string | any[], bbox: number[][]) {
    const tileBounds =
      tile.length === 4 ? tile : tilebelt.tileToBBOX(tile as number[]);
    if (tileBounds[2] < bbox[0][0]) return false;
    if (tileBounds[0] > bbox[1][0]) return false;
    if (tileBounds[3] < bbox[0][1]) return false;
    if (tileBounds[1] > bbox[1][1]) return false;
    return true;
  }

  _getServiceMetadata() {
    if (this.serviceMetadata !== null)
      return Promise.resolve(this.serviceMetadata);
    return new Promise((resolve, reject) => {
      fetch(`${this._esriServiceOptions.url}?f=json`, { cache: "force-cache" })
        .then((response) => response.json())
        .then((data) => {
          this.serviceMetadata = data;
          resolve(this.serviceMetadata);
        })
        .catch((err) => reject(err));
    });
  }

  getGeometryOptions() {
    return {
      sr: 4326,
      geometryType: "esriGeometryPoint",
      geometry: JSON.stringify({
        x: this._geometryOptions?.longitude,
        y: this._geometryOptions?.latitude,
        spatialReference: {
          wkid: 4326,
        },
      }),
      spatialRel: "esriSpatialRelIntersects",
      units: "esriSRUnit_StatuteMile",
      distance: this._geometryOptions?.radius || 50,
    };
  }

  getFeaturesByLonLat(
    lnglat: { lng: number; lat: number },
    radius?: number,
    returnGeometry?: boolean,
  ) {
    returnGeometry = returnGeometry ? returnGeometry : false;
    radius = radius ? radius : 20;
    const params = new URLSearchParams(
      // @ts-ignore
      {
        sr: 4326,
        geometryType: "esriGeometryPoint",
        geometry: JSON.stringify({
          x: lnglat.lng,
          y: lnglat.lat,
          spatialReference: {
            wkid: 4326,
          },
        }),
        returnGeometry,
        time: this._time,
        outFields: "*",
        spatialRel: "esriSpatialRelIntersects",
        units: "esriSRUnit_StatuteMile",
        distance: radius,
        f: "geojson",
      },
    );

    return new Promise((resolve) => {
      this._requestJson(
        `${this._esriServiceOptions.url}/query?${params.toString()}`,
      ).then((data) => resolve(data));
    });
  }

  getFeaturesByObjectIds(objectIds: string, returnGeometry: boolean) {
    if (Array.isArray(objectIds)) objectIds = objectIds.join(",");
    returnGeometry = returnGeometry ? returnGeometry : false;
    // @ts-ignore
    const params = new URLSearchParams({
      sr: 4326,
      objectIds,
      returnGeometry,
      outFields: "*",
      f: "geojson",
    });

    return new Promise((resolve) => {
      this._requestJson(
        `${this._esriServiceOptions.url}/query?${params.toString()}`,
      ).then((data) => resolve(data));
    });
  }

  _projectBounds() {
    // @ts-ignore
    const params = new URLSearchParams({
      geometries: JSON.stringify({
        geometryType: "esriGeometryEnvelope",
        geometries: [this.serviceMetadata.extent],
      }),
      inSR:
        this.serviceMetadata?.extent?.spatialReference?.wkid ||
        this.serviceMetadata?.fullExtent?.spatialReference?.wkid,
      outSR: 4326,
      f: "json",
    });

    this._requestJson(
      `${this._esriServiceOptions.projectionEndpoint}?${params.toString()}`,
    )
      .then((data: any) => {
        const extent = data.geometries[0];
        this._maxExtent = [extent.xmin, extent.ymin, extent.xmax, extent.ymax];
        this._clearAndRefreshTiles();
      })
      .catch(() => {
        this._esriServiceOptions.projectionEndpoint =
          this._fallbackProjectionEndpoint;
        this._projectBounds();
      });
  }

  _requestJson(url: string) {
    return new Promise((resolve, reject) => {
      fetch(url, { cache: "force-cache" })
        .then((response) => response.json())
        .then((data) => {
          if ("error" in data) reject(new Error("Endpoint doesnt exist"));
          resolve(data);
        })
        .catch((error) => reject(error));
    });
  }
}
